import { useState, useEffect, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  useTheme,
  Skeleton,
  Divider,
  Button,
  Stack,
  Dialog,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import TableChartIcon from "@mui/icons-material/TableChart";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddIcon from "@mui/icons-material/Add";
import StorageIcon from "@mui/icons-material/Storage";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  stageClientCsv,
  sendDataChatMessage,
  listClientTables,
  listDataHistory,
  getDataReadiness,
  previewDataFix,
  applyDataFix,
  getDataDictionary,
  patchDataDictionary,
  patchDataDictionaryBatch,
  generateDataDictionary,
  getDatasetSummary,
} from "../utils/api";
import { useImpersonation } from "../contexts/ImpersonationContext";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Drawer from "@mui/material/Drawer";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

// ─── Support links ───────────────────────────────────────────────────────────
// Three Calendly events under calendly.com/assertion-ai/. The right one is
// picked per-finding by bookingUrlFor() based on whether the issue is
// auto-fixable or needs design help.
//
// Calendly's URL pre-fill uses positional answer params: ?a1=<answer to
// first custom question>, ?a2=<second>, ?a3=<third>. Add custom questions
// to each event in this order:
//    Q1: "What's the issue?"   (we pass finding_id)
//    Q2: "Which table?"        (we pass table name)
//    Q3: "Anything else for the meeting?"   (we leave blank — user fills in)
const SUPPORT_LINKS = {
  designSession:   "https://calendly.com/assertion-ai/data-design-session",   // 45 min — structural issues
  quickHelp:       "https://calendly.com/assertion-ai/quick-help",            // 15 min — small fixes
  strategicReview: "https://calendly.com/assertion-ai/strategic-review",      // 60 min — broad / strategy
  deploymentCall:  "https://calendly.com/assertion-ai/deployment-call",       // 30 min — productionizing a model
};

// Pick the right event type based on the finding shape, then attach
// context as Calendly pre-fill params. Callers can also pass an explicit
// fallback when finding is null (e.g. the Data Admin button asks for
// "designSession" specifically; future strategic-review buttons would
// pass "strategicReview").
function bookingUrlFor(finding, fallback = "designSession") {
  let base;
  if (!finding) {
    base = SUPPORT_LINKS[fallback] || SUPPORT_LINKS.designSession;
  } else if (!finding.fix_recipe_id) {
    // Issues without an auto-fix recipe are structural (no PK detectable,
    // no upstream automation, schema design questions). These deserve the
    // longer design session.
    base = SUPPORT_LINKS.designSession;
  } else {
    // Auto-fixable but user wants to talk → 15-min quick help.
    base = SUPPORT_LINKS.quickHelp;
  }
  const params = new URLSearchParams();
  if (finding?.finding_id) params.set("a1", finding.finding_id);
  if (finding?.table) params.set("a2", finding.table);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

function _scoreColor(score, theme) {
  if (score === "excellent" || score === "ready") return theme.palette.success.main;
  if (score === "yellow") return theme.palette.warning.main;
  if (score === "not_ready") return theme.palette.error.main;
  return theme.palette.text.disabled;
}

function _scoreLabel(score) {
  return {
    excellent: "Excellent",
    ready: "Ready",
    yellow: "Yellow",
    not_ready: "Not ready",
  }[score] || score || "Unknown";
}

function _staleness(scoredAtIso) {
  if (!scoredAtIso) return { label: "Never checked", tone: "error" };
  const ageMs = Date.now() - new Date(scoredAtIso).getTime();
  const hours = ageMs / (1000 * 60 * 60);
  if (hours < 1) return { label: "just now", tone: "success" };
  if (hours < 6) return { label: `${Math.round(hours)}h ago`, tone: "success" };
  if (hours < 24) return { label: `${Math.round(hours)}h ago`, tone: "default" };
  return { label: `${Math.round(hours / 24)}d ago`, tone: "warning" };
}

// ─── Relative time helper ─────────────────────────────────────────────────────

function relativeTime(isoString) {
  if (!isoString) return "";
  const diff = Date.now() - new Date(isoString).getTime();
  const hours = diff / (1000 * 60 * 60);
  if (hours < 1) return "just now";
  if (hours < 24) return `${Math.floor(hours)}h ago`;
  const d = new Date(isoString);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ─── Chat message bubble ──────────────────────────────────────────────────────

function ChatBubble({ message }) {
  const theme = useTheme();
  const isUser = message.role === "user";
  const displayContent = isUser
    ? message.content.replace(/\n\n\[File attached:[\s\S]*$/, "").trim()
    : message.content;

  return (
    <Box sx={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", mb: 1.5 }}>
      <Box
        sx={{
          maxWidth: "85%",
          px: 2,
          py: 1.25,
          borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
          bgcolor: isUser ? "primary.main" : theme.palette.background.paper,
          color: isUser ? "#fff" : "text.primary",
          border: isUser ? "none" : `1px solid ${theme.palette.divider}`,
          "& p": { m: 0 },
          "& p + p": { mt: 1 },
          "& ul, & ol": { pl: 2.5, my: 0.5 },
          "& code": {
            bgcolor: isUser ? "rgba(255,255,255,0.15)" : "action.hover",
            px: 0.5,
            borderRadius: 0.5,
            fontFamily: "monospace",
            fontSize: "0.85em",
          },
          "& pre": { m: 0, overflow: "auto" },
          "& strong": { fontWeight: 700 },
        }}
      >
        {isUser ? (
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{displayContent}</Typography>
        ) : (
          <Typography variant="body2" component="div">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayContent}</ReactMarkdown>
          </Typography>
        )}
        {message.streaming && (
          <Box component="span" sx={{ display: "inline-block", width: 8, height: 14, bgcolor: "currentColor", ml: 0.5, animation: "blink 1s step-end infinite", "@keyframes blink": { "50%": { opacity: 0 } } }} />
        )}
      </Box>
    </Box>
  );
}

// ─── Readiness banner (Dictionary top) ───────────────────────────────────────
// One-line summary that jumps the user to the Quality tab. Business users
// want the trust headline ("are we good?") without leaving Dictionary.

function ReadinessBanner({ report, onJumpToQuality }) {
  const theme = useTheme();
  if (!report) return null;
  const modeling = report.modeling_score || "unknown";
  const deployment = report.deployment_score || "unknown";
  const palette = (s) => {
    if (s === "excellent" || s === "ready") return { color: theme.palette.success.main, label: "Ready", Icon: CheckCircleOutlineIcon };
    if (s === "yellow") return { color: theme.palette.warning.main, label: "Yellow", Icon: WarningAmberIcon };
    if (s === "not_ready") return { color: theme.palette.error.main, label: "Not ready", Icon: ErrorOutlineIcon };
    return { color: theme.palette.text.disabled, label: "Unknown", Icon: HelpOutlineIcon };
  };
  const m = palette(modeling), d = palette(deployment);
  return (
    <Box
      onClick={onJumpToQuality}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
        px: 2, py: 1.25,
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: "pointer",
        "&:hover": { bgcolor: theme.palette.action.hover },
      }}
    >
      <m.Icon sx={{ fontSize: 16, color: m.color }} />
      <Typography variant="body2" sx={{ fontSize: 13 }}>
        Modeling: <Box component="span" sx={{ fontWeight: 600, color: m.color }}>{m.label}</Box>
      </Typography>
      <Box sx={{ width: 1, height: 12, bgcolor: "divider" }} />
      <d.Icon sx={{ fontSize: 16, color: d.color }} />
      <Typography variant="body2" sx={{ fontSize: 13 }}>
        Deployment: <Box component="span" sx={{ fontWeight: 600, color: d.color }}>{d.label}</Box>
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
        See details →
      </Typography>
    </Box>
  );
}


// ─── Dictionary tab ──────────────────────────────────────────────────────────
// Primary view for business users: "what data do I have?". Flat list grouped
// by table, descriptions visible on every row (the whole point), with jump
// chips and a readiness banner at top.
//
// Inline description editing is handled at the row level (DictionaryRow).
// Structural fields (PK/FK, role) are auto-detected and read-only in this UI —
// changing them requires re-running the dict generator with a corrected
// schema, which the chat agent handles via its data_agent tools.

const _staleDaysCutoff = 30;

function _formatRelativeDays(isoLike) {
  if (!isoLike) return null;
  const t = new Date(isoLike).getTime();
  if (!t) return null;
  const days = Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} mo ago`;
  return `${Math.floor(days / 365)} yr ago`;
}

// "2024-01-15" → "Jan 2024 — May 2026 (28 months)". Tolerant of TIMESTAMP /
// DATETIME strings; falls back to the raw string if Date can't parse.
function _formatCoverage(min, max) {
  const tryParse = (s) => {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };
  const dMin = tryParse(min);
  const dMax = tryParse(max);
  if (!dMin || !dMax) return `${min} — ${max}`;
  const fmt = (d) => d.toLocaleString("en-US", { month: "short", year: "numeric" });
  const months = (dMax.getFullYear() - dMin.getFullYear()) * 12
    + (dMax.getMonth() - dMin.getMonth()) + 1;
  let span;
  if (months < 24) span = `${months} months`;
  else if (months % 12 === 0) span = `${months / 12} years`;
  else span = `${(months / 12).toFixed(1)} years`;
  return `${fmt(dMin)} — ${fmt(dMax)}  (${span})`;
}

function DictionarySection({
  dictionary, loading, error, readiness, onJumpToQuality, scrollToTable,
  onSaveDescription, onSaveDescriptionsBatch,
}) {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [showAllByTable, setShowAllByTable] = useState({});  // tableName -> true (column-list overflow)
  // BROWSE COLUMNS: each table collapsed by default. Click a table's row in
  // OVERVIEW, or its own header in BROWSE COLUMNS, to expand. Search-matching
  // tables auto-expand for the duration of the search.
  const [expandedTables, setExpandedTables] = useState(() => new Set());
  const sectionRef = useRef(null);

  // AI-draft state: drafts[tableName] = { table_description, columns: {colName: description} }
  // Drafts are NOT persisted — they're suggestions the user reviews and accepts.
  const [drafts, setDrafts] = useState({});
  const [generatingTable, setGeneratingTable] = useState(null);
  const [generateError, setGenerateError] = useState(null);

  // Dataset-level AI summary (Tier 3): cached server-side per (org, dataset).
  const [datasetSummary, setDatasetSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  // Auto-fetch the summary on mount IF it's already cached server-side.
  // The endpoint returns from_cache=true instantly; if not cached, we don't
  // trigger an LLM call until the user clicks the button. (Backend distinguishes
  // by passing payload {} — currently it always calls LLM if no cache, which
  // means we have to gate on the button. So skip auto-fetch entirely.)

  const handleGenerateSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const result = await getDatasetSummary();
      setDatasetSummary(result);
    } catch (e) {
      setSummaryError(e?.response?.data?.error || e?.message || "Summary failed");
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const handleGenerate = useCallback(async (tableName) => {
    setGeneratingTable(tableName);
    setGenerateError(null);
    try {
      const result = await generateDataDictionary({ table: tableName });
      const colsMap = {};
      (result.columns || []).forEach((c) => {
        if (c.description) colsMap[c.name] = c.description;
      });
      setDrafts((prev) => ({
        ...prev,
        [tableName]: {
          table_description: result.table_description || "",
          columns: colsMap,
        },
      }));
    } catch (e) {
      setGenerateError(e?.response?.data?.error || e?.message || "Generate failed");
    } finally {
      setGeneratingTable(null);
    }
  }, []);

  const handleAcceptDraft = useCallback(async (tableName, columnName) => {
    const draft = drafts[tableName]?.columns?.[columnName];
    if (!draft) return;
    await onSaveDescription({ table: tableName, column: columnName, description: draft });
    setDrafts((prev) => {
      const next = { ...prev };
      const t = { ...(next[tableName] || {}) };
      const cols = { ...(t.columns || {}) };
      delete cols[columnName];
      t.columns = cols;
      next[tableName] = t;
      return next;
    });
  }, [drafts, onSaveDescription]);

  const handleAcceptAllDrafts = useCallback(async (tableName) => {
    const tableDrafts = drafts[tableName]?.columns || {};
    const edits = Object.keys(tableDrafts).map((n) => ({
      column: n, description: tableDrafts[n],
    }));
    if (!edits.length) return;
    // One request → one GCS write. Looping N single PATCHes tripped a 429
    // (per-object mutation rate limit) and silently failed.
    try {
      await onSaveDescriptionsBatch({ table: tableName, edits });
    } catch (e) {
      setGenerateError(e?.response?.data?.error || e?.message || "Accept all failed");
      return;
    }
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[tableName];
      return next;
    });
  }, [drafts, onSaveDescriptionsBatch]);

  const handleDiscardDraft = useCallback((tableName, columnName) => {
    setDrafts((prev) => {
      const next = { ...prev };
      const t = { ...(next[tableName] || {}) };
      const cols = { ...(t.columns || {}) };
      if (columnName) {
        delete cols[columnName];
        t.columns = cols;
        next[tableName] = t;
      } else {
        delete next[tableName];
      }
      return next;
    });
  }, []);

  // Scroll to a specific table when caller asks
  useEffect(() => {
    if (!scrollToTable || !sectionRef.current) return;
    const el = sectionRef.current.querySelector(`[data-table="${scrollToTable}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [scrollToTable]);

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="50%" />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Your data</Typography>
        <Alert severity="warning">{String(error)}</Alert>
      </Paper>
    );
  }

  const tables = dictionary?.tables || [];
  const totalCols = tables.reduce((n, t) => n + (t.columns?.length || 0), 0);
  const totalRows = tables.reduce((n, t) => n + (t.row_count || 0), 0);

  // Sort each table's columns: PK first, then FK, then alphabetical.
  // Severity is intentionally NOT a sort key here — Dictionary is for browsing.
  const sortedTables = tables.map((t) => {
    const sorted = [...(t.columns || [])].sort((a, b) => {
      if (a.is_pk && !b.is_pk) return -1;
      if (b.is_pk && !a.is_pk) return 1;
      if (a.is_fk && !b.is_fk) return -1;
      if (b.is_fk && !a.is_fk) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
    return { ...t, columns: sorted };
  });

  // Search across name / type / description / table name. Preserves grouping.
  const q = search.trim().toLowerCase();
  const filtered = q
    ? sortedTables
        .map((t) => {
          const tableMatches = (t.name || "").toLowerCase().includes(q);
          const cols = (t.columns || []).filter((c) =>
            tableMatches ||
            (c.name || "").toLowerCase().includes(q) ||
            (c.description || "").toLowerCase().includes(q)
          );
          return { ...t, columns: cols };
        })
        .filter((t) => (t.columns || []).length > 0)
    : sortedTables;

  return (
    <Paper variant="outlined" sx={{ p: 0, overflow: "hidden" }} ref={sectionRef}>
      {/* Sticky header strip: title + counts + search */}
      <Box sx={{
        px: 2, pt: 2, pb: 1.5,
        position: "sticky", top: 0, zIndex: 3,
        bgcolor: "background.paper",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}>
        <Box sx={{ display: "flex", alignItems: "baseline", mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
            Your data
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {tables.length} tables · {totalCols} columns · {totalRows.toLocaleString()} rows
          </Typography>
        </Box>
        <TextField
          fullWidth size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search columns, types, descriptions…"
          InputProps={{
            startAdornment: <Box sx={{ color: "text.disabled", mr: 1, fontSize: 14 }}>🔍</Box>,
          }}
          sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
        />
      </Box>

      {/* Readiness banner — soft trust signal that links to Quality */}
      {readiness?.report && (
        <ReadinessBanner report={readiness.report} onJumpToQuality={onJumpToQuality} />
      )}

      {/* ═══ OVERVIEW ═══ — at-a-glance view of what's in the dataset.
           Each row is clickable: jumps to + expands that table in BROWSE COLUMNS. */}
      {tables.length > 0 && (
        <Box sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === "dark" ? "#15181c" : "#fafbfc",
        }}>
          <Box sx={{
            px: 2, py: 1.25,
            display: "flex", alignItems: "baseline", gap: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1, fontSize: 11 }}>
              Overview
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
              click a table to see its columns
            </Typography>
          </Box>
          {/* Dataset-level time coverage (when backend detected a date column anywhere) */}
          {dictionary?.time_coverage?.min && dictionary?.time_coverage?.max && (
            <Box sx={{
              px: 2, py: 0.75,
              display: "flex", alignItems: "center", gap: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              fontSize: 13,
            }}>
              <Typography variant="caption" color="text.disabled" sx={{
                fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
                minWidth: 110, flexShrink: 0,
              }}>
                Time coverage
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 13 }}>
                {_formatCoverage(dictionary.time_coverage.min, dictionary.time_coverage.max)}
              </Typography>
            </Box>
          )}

          {/* AI-generated dataset summary + suggested questions (Tier 3) */}
          {datasetSummary ? (
            <Box sx={{
              px: 2, py: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                <AutoAwesomeIcon sx={{ fontSize: 14, color: "primary.main", mt: 0.25, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.55 }}>
                  {datasetSummary.summary}
                </Typography>
              </Box>
              {datasetSummary.questions?.length > 0 && (
                <Box sx={{ mt: 1, ml: 3 }}>
                  <Typography variant="caption" color="text.disabled" sx={{
                    fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
                    display: "block", mb: 0.5,
                  }}>
                    Questions this data can answer
                  </Typography>
                  <Stack spacing={0.5}>
                    {datasetSummary.questions.map((q, i) => (
                      <Typography key={i} variant="body2" sx={{
                        fontSize: 13, color: "text.secondary",
                        "&::before": { content: '"• "', color: "text.disabled" },
                      }}>
                        {q}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{
              px: 2, py: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex", alignItems: "center",
            }}>
              <Button
                size="small" variant="text"
                startIcon={summaryLoading
                  ? <CircularProgress size={12} />
                  : <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                }
                disabled={summaryLoading}
                onClick={handleGenerateSummary}
                sx={{ textTransform: "none", fontSize: 12 }}>
                {summaryLoading ? "Generating…" : "Generate dataset summary"}
              </Button>
              {summaryError && (
                <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                  {summaryError}
                </Typography>
              )}
            </Box>
          )}
          {tables.map((t) => {
            const rel = _formatRelativeDays(t.last_loaded_at);
            const stale = t.last_loaded_at
              && (Date.now() - new Date(t.last_loaded_at).getTime()) > _staleDaysCutoff * 86400000;
            return (
              <Box
                key={t.name}
                onClick={() => {
                  setExpandedTables((prev) => new Set(prev).add(t.name));
                  // Defer scroll one frame so the just-expanded section is laid out
                  setTimeout(() => {
                    const el = sectionRef.current?.querySelector(`[data-table="${t.name}"]`);
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 0);
                }}
                sx={{
                  px: 2, py: 0.75,
                  display: "flex", alignItems: "center", gap: 1.5,
                  cursor: "pointer", fontSize: 13,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  "&:last-of-type": { borderBottom: "none" },
                  "&:hover": { bgcolor: theme.palette.action.hover },
                }}
              >
                <TableChartIcon sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }} />
                <Box sx={{ fontFamily: "monospace", fontWeight: 600, minWidth: 0, flexShrink: 0 }}>
                  {t.name}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {(t.row_count || 0).toLocaleString()} rows
                  {rel ? ` · ${rel}` : ""}
                </Typography>
                {stale && (
                  <Chip label="stale" size="small" variant="outlined"
                    sx={{ fontSize: 10, height: 16, color: theme.palette.warning.main, borderColor: theme.palette.warning.main, flexShrink: 0 }} />
                )}
                <Typography variant="caption" color="text.secondary" sx={{
                  flex: 1, minWidth: 0,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {t.description || <Box component="span" sx={{ color: "text.disabled" }}>No description</Box>}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ═══ BROWSE COLUMNS ═══ — opt-in column detail. Per-table sections are
           collapsed by default; click a sticky header (or any OVERVIEW row) to expand.
           Active search auto-expands all matching tables. */}
      {tables.length > 0 && (
        <Box sx={{
          px: 2, py: 1.25,
          display: "flex", alignItems: "baseline", gap: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1, fontSize: 11 }}>
            Browse columns
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
            click a table to expand its columns · use ✨ to draft descriptions
          </Typography>
          {expandedTables.size > 0 && (
            <Button size="small" variant="text"
              onClick={() => setExpandedTables(new Set())}
              sx={{ textTransform: "none", fontSize: 11, py: 0.1, minWidth: 0 }}>
              Collapse all
            </Button>
          )}
        </Box>
      )}

      {generateError && (
        <Alert severity="warning" onClose={() => setGenerateError(null)} sx={{ borderRadius: 0 }}>
          {generateError}
        </Alert>
      )}

      {filtered.length === 0 && q && (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No matches for <code style={{ fontFamily: "monospace" }}>{search}</code>
          </Typography>
          <Button size="small" sx={{ textTransform: "none", mt: 1 }} onClick={() => setSearch("")}>
            Clear search
          </Button>
        </Box>
      )}

      {/* Per-table sections — collapsed by default unless expandedTables or search */}
      <Box>
        {filtered.map((t) => {
          const allCols = t.columns || [];
          const showAll = !!showAllByTable[t.name] || !!q || allCols.length <= 15;
          const visibleCols = showAll ? allCols : allCols.slice(0, 10);
          const relDays = _formatRelativeDays(t.last_loaded_at);
          const isStale = t.last_loaded_at
            && (Date.now() - new Date(t.last_loaded_at).getTime()) > _staleDaysCutoff * 86400000;
          // Effective expansion: explicit toggle OR active search auto-expands matches.
          const isExpanded = expandedTables.has(t.name) || !!q;
          const toggleExpand = (e) => {
            // Don't toggle if the user clicked an action button inside the header.
            if (e.target.closest && e.target.closest("[data-no-toggle]")) return;
            setExpandedTables((prev) => {
              const next = new Set(prev);
              if (next.has(t.name)) next.delete(t.name);
              else next.add(t.name);
              return next;
            });
          };
          return (
            <Box key={t.name} data-table={t.name}>
              {/* Sticky table header — click to expand/collapse this table's columns */}
              <Box
                onClick={toggleExpand}
                sx={{
                  position: "sticky", top: 88, zIndex: 2,
                  display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1,
                  px: 2, py: 1.25,
                  bgcolor: theme.palette.mode === "dark" ? "#15181c" : "#fafbfc",
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  cursor: "pointer",
                  "&:hover": { bgcolor: theme.palette.action.hover },
                }}
              >
                <ExpandMoreIcon sx={{
                  fontSize: 16, color: "text.secondary",
                  transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.15s ease",
                }} />
                <TableChartIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Box sx={{ fontFamily: "monospace", fontWeight: 600, fontSize: 13 }}>{t.name}</Box>
                <Typography variant="caption" color="text.secondary">
                  {allCols.length} cols · {(t.row_count || 0).toLocaleString()} rows
                  {relDays ? ` · ${relDays}` : ""}
                </Typography>
                {isStale && (
                  <Chip label="stale" size="small" variant="outlined"
                    sx={{ fontSize: 10, height: 18, color: theme.palette.warning.main, borderColor: theme.palette.warning.main }} />
                )}
                {!t.has_curated_dict && (
                  <Chip label="schema only" size="small" variant="outlined"
                    sx={{ fontSize: 10, height: 18, color: "text.disabled" }} />
                )}
                <Box sx={{ flex: 1 }} />
                {drafts[t.name]?.columns && Object.keys(drafts[t.name].columns).length > 0 && (
                  <Box data-no-toggle sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                      {Object.keys(drafts[t.name].columns).length} draft{Object.keys(drafts[t.name].columns).length === 1 ? "" : "s"}
                    </Typography>
                    <Button size="small" variant="text" color="primary"
                      onClick={(e) => { e.stopPropagation(); handleAcceptAllDrafts(t.name); }}
                      sx={{ textTransform: "none", fontSize: 11, py: 0.1, minWidth: 0 }}>
                      Accept all
                    </Button>
                    <Button size="small" variant="text"
                      onClick={(e) => { e.stopPropagation(); handleDiscardDraft(t.name, null); }}
                      sx={{ textTransform: "none", fontSize: 11, py: 0.1, minWidth: 0, color: "text.secondary" }}>
                      Discard
                    </Button>
                  </Box>
                )}
                <Box data-no-toggle>
                  <Button
                    size="small" variant="text"
                    startIcon={
                      generatingTable === t.name
                        ? <CircularProgress size={12} />
                        : <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                    }
                    disabled={generatingTable === t.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Expand on generate so the user immediately sees drafts populate
                      setExpandedTables((prev) => new Set(prev).add(t.name));
                      handleGenerate(t.name);
                    }}
                    sx={{ textTransform: "none", fontSize: 11, py: 0.1 }}>
                    {generatingTable === t.name ? "Generating…" : "Generate descriptions"}
                  </Button>
                </Box>
              </Box>
              {/* Optional table description — only when expanded */}
              {isExpanded && t.description && (
                <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="body2" sx={{ fontSize: 13, color: "text.secondary" }}>
                    {t.description}
                  </Typography>
                </Box>
              )}
              {/* Column rows — two-line blocks. Only rendered when expanded
                  (or when search filters this table). */}
              {isExpanded && visibleCols.map((c) => (
                <DictionaryRow
                  key={c.name}
                  table={t.name}
                  column={c}
                  draft={drafts[t.name]?.columns?.[c.name] || null}
                  onSaveDescription={onSaveDescription}
                  onAcceptDraft={() => handleAcceptDraft(t.name, c.name)}
                  onDiscardDraft={() => handleDiscardDraft(t.name, c.name)}
                />
              ))}
              {/* Show more */}
              {isExpanded && !showAll && allCols.length > visibleCols.length && (
                <Box sx={{
                  px: 2, py: 1, textAlign: "center",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.mode === "dark" ? "#13161a" : "#fdfdfd",
                }}>
                  <Button size="small" variant="text"
                    onClick={() => setShowAllByTable({ ...showAllByTable, [t.name]: true })}
                    endIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                    sx={{ textTransform: "none", fontSize: 12 }}>
                    Show all {allCols.length} columns
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

// One column = two lines. Line 1: name + type + role chips. Line 2: description.
// Description is click-to-edit inline (no mode switch, no separate page).
//   - Click description text → becomes editable
//   - ⏎ or blur → save · Esc → cancel
//   - Empty rows show "+ Add description" placeholder
function DictionaryRow({
  table, column: c, draft: aiDraft,
  onSaveDescription, onAcceptDraft, onDiscardDraft,
}) {
  const theme = useTheme();
  const hasDesc = !!(c.description && c.description.trim());
  const hasAiDraft = !!aiDraft;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.description || "");
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const inputRef = useRef(null);

  // Keep draft in sync if the row's value changes externally (e.g. AI generate)
  useEffect(() => {
    if (!editing) setDraft(c.description || "");
  }, [c.description, editing]);

  const begin = () => {
    setDraft(c.description || "");
    setErrorMsg(null);
    setEditing(true);
  };
  const cancel = () => {
    setDraft(c.description || "");
    setEditing(false);
    setErrorMsg(null);
  };
  const commit = async () => {
    const next = (draft || "").trim();
    if (next === (c.description || "").trim()) {
      setEditing(false);
      // If we were editing an AI draft and the user committed without
      // changes, treat that as Accept — discard the draft so it doesn't
      // re-appear over the saved value.
      if (hasAiDraft && onDiscardDraft) onDiscardDraft();
      return;
    }
    setSaving(true);
    setErrorMsg(null);
    try {
      await onSaveDescription({ table, column: c.name, description: next });
      setEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
      // Discard any AI draft once a real value has been saved.
      if (hasAiDraft && onDiscardDraft) onDiscardDraft();
    } catch (e) {
      setErrorMsg(e?.response?.data?.error || e?.message || "Save failed");
      // Stay in edit mode so the user can fix and retry
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{
      px: 2, py: 1,
      borderBottom: `1px solid ${theme.palette.divider}`,
      "&:hover": { bgcolor: theme.palette.action.hover },
    }}>
      {/* Line 1: name + type + role chips */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13 }}>
        <Box sx={{ fontFamily: "monospace", fontWeight: 500 }}>{c.name}</Box>
        <Chip label={c.type || "—"} size="small" variant="outlined"
          sx={{ fontSize: 10, height: 18, fontFamily: "monospace" }} />
        {c.is_pk && (
          <Tooltip title="Primary key (auto-detected)" arrow>
            <Chip label="PK" size="small" color="primary" variant="outlined"
              sx={{ fontSize: 10, height: 18, fontWeight: 600, cursor: "help" }} />
          </Tooltip>
        )}
        {c.is_fk && (
          <Tooltip title={`Foreign key${c.fk_ref ? " → " + c.fk_ref : " (auto-detected)"}`} arrow>
            <Chip
              label={c.fk_ref ? `→ ${c.fk_ref}` : "FK"}
              size="small" variant="outlined"
              sx={{ fontSize: 10, height: 18, fontFamily: "monospace", cursor: "help" }}
            />
          </Tooltip>
        )}
        {justSaved && (
          <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "success.main", ml: 0.5 }} />
        )}
      </Box>
      {/* Line 2: AI draft (if present), or editable description */}
      {hasAiDraft && !editing ? (
        <Box sx={{
          mt: 0.5, ml: 0.5, p: 1, borderRadius: 0.5,
          bgcolor: theme.palette.mode === "dark" ? "rgba(120, 144, 200, 0.08)" : "rgba(120, 144, 200, 0.06)",
          border: `1px dashed ${theme.palette.primary.main}`,
        }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 14, color: "primary.main", mt: 0.25, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ flex: 1, fontSize: 13 }}>
              {aiDraft}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, mt: 0.75, justifyContent: "flex-end" }}>
            <Button size="small" variant="text"
              onClick={async () => {
                try { await onAcceptDraft(); } catch (e) { setErrorMsg(e?.message || "Save failed"); }
              }}
              sx={{ textTransform: "none", fontSize: 11, py: 0.1, minWidth: 0 }}>
              ✓ Accept
            </Button>
            <Button size="small" variant="text"
              onClick={() => { setDraft(aiDraft); setEditing(true); }}
              sx={{ textTransform: "none", fontSize: 11, py: 0.1, minWidth: 0, color: "text.secondary" }}>
              Edit
            </Button>
            <Button size="small" variant="text"
              onClick={onDiscardDraft}
              sx={{ textTransform: "none", fontSize: 11, py: 0.1, minWidth: 0, color: "text.secondary" }}>
              ✕ Discard
            </Button>
          </Box>
          {errorMsg && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
              {errorMsg}
            </Typography>
          )}
        </Box>
      ) : editing ? (
        <Box sx={{ mt: 0.5, ml: 0.5 }}>
          <TextField
            inputRef={inputRef}
            autoFocus
            fullWidth
            size="small"
            multiline
            maxRows={4}
            value={draft}
            disabled={saving}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                commit();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              }
            }}
            onBlur={() => { if (!saving) commit(); }}
            placeholder="Describe this column…"
            sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
          />
          {errorMsg && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
              {errorMsg}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.25 }}>
            ⏎ to save · Esc to cancel
          </Typography>
        </Box>
      ) : hasDesc ? (
        <Typography
          variant="body2"
          onClick={begin}
          sx={{
            mt: 0.5, ml: 0.5, fontSize: 13, color: "text.secondary",
            cursor: "text", borderRadius: 0.5, px: 0.5, mx: -0.5,
            "&:hover": { bgcolor: theme.palette.action.selected },
          }}
        >
          {c.description}
        </Typography>
      ) : (
        <Box
          onClick={begin}
          sx={{
            mt: 0.5, ml: 0.5, py: 0.25, px: 0.5, mx: -0.5,
            cursor: "text", borderRadius: 0.5,
            "&:hover": { bgcolor: theme.palette.action.selected },
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 12, color: "text.disabled" }}>
            + Add description
          </Typography>
        </Box>
      )}
    </Box>
  );
}


// ─── Quality tab ─────────────────────────────────────────────────────────────
// For data admins: "is the data ready, what needs fixing?". Opens with
// readiness scores, then the actionable findings list. Activity, privacy,
// and help follow as context.

function QualityTab({
  history, readiness, readinessLoading, readinessError,
  onRefreshReadiness, onFix, onBookSession,
}) {
  const theme = useTheme();
  const findings = readiness?.report?.findings || [];
  const privacyFindings = findings.filter((f) => f.category === "privacy");

  return (
    <Stack spacing={2}>
      {/* Readiness scores — modeling + deployment */}
      <ReadinessHeader
        report={readiness?.report}
        fromCache={readiness?.from_cache}
        loading={readinessLoading}
        onRefresh={onRefreshReadiness}
      />
      {readinessError && (
        <Alert severity="warning" onClose={() => {}}>
          {readinessError}
        </Alert>
      )}

      {/* Actionable findings list — the core value of this tab */}
      {readiness?.report && (
        <ActionsPanel
          findings={findings}
          onFix={onFix}
          onBookSession={onBookSession}
        />
      )}

      {/* Privacy callout (only when present) */}
      {privacyFindings.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
              Privacy review
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {privacyFindings.length} flagged
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            Columns that look like PII (email, phone, SSN, IP) but aren't flagged in the dictionary.
          </Typography>
          <Stack spacing={0.75}>
            {privacyFindings.map((f, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
                <WarningAmberIcon sx={{ color: "warning.main", fontSize: 16 }} />
                <Typography variant="body2" sx={{ flex: 1, fontSize: 13 }}>
                  {f.title}
                </Typography>
                <Chip label={f.table} size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Recent activity — compact, contextual */}
      {history && history.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Recent activity
          </Typography>
          <Stack spacing={0.5}>
            {history.slice(0, 8).map((entry, i) => (
              <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center", fontSize: 13 }}>
                <Typography variant="body2" sx={{ flex: 1, fontSize: 12 }}>
                  {entry.action || "load"} → <code style={{ fontFamily: "monospace" }}>{entry.table_name}</code>
                  {entry.row_count != null && (
                    <Typography component="span" variant="caption" color="text.secondary">
                      {" "}· {entry.row_count.toLocaleString()} rows
                    </Typography>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {relativeTime(entry.committed_at)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Help / Calendly card — escape hatch at the bottom */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <CalendarMonthIcon sx={{ color: theme.palette.primary.main, mt: 0.25 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Need a conversation about your data?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Some issues — missing primary keys, conceptual schema mismatches,
              setting up a recurring data source — are easier to talk through
              with a data designer. Book a 45-minute session.
            </Typography>
            <Button variant="outlined" size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(bookingUrlFor(null), "_blank", "noopener")}
              sx={{ textTransform: "none", mt: 1.5 }}
            >
              Book a session
            </Button>
          </Box>
        </Box>
      </Paper>
    </Stack>
  );
}


// ─── Fix drawer (Phase B) ────────────────────────────────────────────────────
// Right-side slide-in drawer that:
//  1. Shows the issue from the action card (read-only context)
//  2. Fetches the recipe preview SQL via POST /data/action/preview
//  3. Lets the user inspect the SQL, optionally edit recipe params for some
//     recipes (e.g. supply a default value for backfill_nulls), then Apply
//  4. Calls POST /data/action/apply; reports outcome and triggers a readiness
//     refresh on the parent.

function FixDrawer({ open, finding, onClose, onApplied }) {
  const theme = useTheme();
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  // For backfill_nulls: editable default value
  const [paramOverrides, setParamOverrides] = useState({});

  useEffect(() => {
    if (!open || !finding) {
      setPreview(null);
      setPreviewError(null);
      setApplyResult(null);
      setParamOverrides({});
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    setApplyResult(null);
    previewDataFix({
      recipe_id: finding.fix_recipe_id,
      table: finding.table,
      params: { ...(finding.fix_params || {}) },
    })
      .then(setPreview)
      .catch((e) => setPreviewError(e?.response?.data?.error || e.message || "Could not load preview"))
      .finally(() => setPreviewLoading(false));
  }, [open, finding]);

  const handleApply = useCallback(async () => {
    if (!finding) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const result = await applyDataFix({
        recipe_id: finding.fix_recipe_id,
        table: finding.table,
        params: { ...(finding.fix_params || {}), ...paramOverrides },
      });
      setApplyResult(result);
      if (result?.success && onApplied) onApplied(result);
    } catch (e) {
      setApplyResult({ success: false, error: e?.response?.data?.error || e.message || "Apply failed" });
    } finally {
      setApplying(false);
    }
  }, [finding, paramOverrides, onApplied]);

  // backfill_nulls: surface a small editor for the default value
  const needsBackfillEditor = finding?.fix_recipe_id === "backfill_nulls";
  const defaultBackfill = paramOverrides.default ?? finding?.fix_params?.default ?? "";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      PaperProps={{ sx: { width: { xs: "100%", sm: 460 }, p: 0 } }}
    >
      {finding && (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", p: 2, borderBottom: `1px solid ${theme.palette.divider}`, gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
              Fix · {finding.title || finding.finding_id}
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Issue context */}
          <Box sx={{ p: 2, pb: 0 }}>
            <Typography variant="caption" color="text.secondary">Issue</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {finding.detail || finding.title}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap" }}>
              <Chip label={finding.table} size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
              {(finding.columns || []).map((c) => (
                <Chip key={c} label={c} size="small" variant="outlined" sx={{ fontSize: 10, height: 20, fontFamily: "monospace" }} />
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Backfill editor (recipe-specific) */}
          {needsBackfillEditor && (
            <Box sx={{ px: 2, pb: 2 }}>
              <Typography variant="caption" color="text.secondary">Default value to backfill</Typography>
              <TextField
                fullWidth size="small" value={defaultBackfill}
                onChange={(e) => setParamOverrides((p) => ({ ...p, default: e.target.value }))}
                placeholder="e.g. 0, unknown, false"
                sx={{ mt: 0.5 }}
              />
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: "block" }}>
                Strings will be quoted; numbers passed as-is; leave empty to use NULL.
              </Typography>
            </Box>
          )}

          {/* Preview SQL */}
          <Box sx={{ px: 2, pb: 2, flex: 1, overflowY: "auto" }}>
            <Typography variant="caption" color="text.secondary">SQL to run</Typography>
            {previewLoading && (
              <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center" }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">Generating preview…</Typography>
              </Box>
            )}
            {previewError && (
              <Alert severity="error" sx={{ mt: 1 }}>{previewError}</Alert>
            )}
            {preview && !previewLoading && (
              <>
                <Box sx={{
                  mt: 1, p: 1.5, bgcolor: theme.palette.mode === "dark" ? "#0e1116" : "#f6f8fa",
                  borderRadius: 1, fontFamily: "monospace", fontSize: 12,
                  whiteSpace: "pre-wrap", overflowX: "auto",
                  border: `1px solid ${theme.palette.divider}`,
                }}>
                  {preview.sql}
                </Box>
                {preview.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    {preview.description}
                  </Typography>
                )}
                {preview.destructive && (
                  <Alert severity="warning" sx={{ mt: 1.5 }} icon={<WarningAmberIcon fontSize="small" />}>
                    This change is destructive — the table will be modified in place. No automatic undo.
                  </Alert>
                )}
              </>
            )}

            {/* Apply result */}
            {applyResult && (
              <Box sx={{ mt: 2 }}>
                <Alert severity={applyResult.success ? "success" : "error"}>
                  {applyResult.success
                    ? (applyResult.message || "Applied.")
                    : (applyResult.error || "Apply failed.")}
                </Alert>
                {applyResult.success && applyResult.rows_affected != null && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Rows affected: {applyResult.rows_affected}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Action footer */}
          <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, display: "flex", gap: 1 }}>
            <Button variant="text" size="small" onClick={onClose} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            {applyResult?.success ? (
              <Button variant="contained" size="small" color="success" onClick={onClose}
                sx={{ textTransform: "none", boxShadow: "none" }}>
                Done
              </Button>
            ) : (
              <Button
                variant="contained" size="small" color="primary"
                startIcon={applying ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                disabled={!preview || applying}
                onClick={handleApply}
                sx={{ textTransform: "none", boxShadow: "none" }}
              >
                {applying ? "Applying…" : "Apply"}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
}


// ─── Readiness header (Phase A) ──────────────────────────────────────────────

function ReadinessHeader({ report, fromCache, loading, onRefresh }) {
  if (loading && !report) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Skeleton variant="text" width="50%" />
        <Skeleton variant="text" width="40%" />
      </Paper>
    );
  }
  if (!report) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Readiness has not been measured yet for this dataset.
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={onRefresh}
          sx={{ mt: 1, textTransform: "none" }}>
          Measure now
        </Button>
      </Paper>
    );
  }
  const staleness = _staleness(report.scored_at);
  const findings = report.findings || [];
  const nCritical = findings.filter((f) => f.severity === "critical").length;
  const nWarning = findings.filter((f) => f.severity === "warning").length;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <ReadinessBadge label="Modeling" score={report.modeling_score} />
        <ReadinessBadge label="Deployment" score={report.deployment_score} />
        <Box sx={{ flex: 1 }} />
        <Chip label={`${nCritical} critical · ${nWarning} warnings`}
              size="small" variant="outlined"
              sx={{ fontSize: 12 }} />
        <Tooltip title={`Last checked ${staleness.label}${fromCache ? " (cached)" : ""}`}>
          <Typography variant="caption" color={`${staleness.tone}.main`}>
            {staleness.label}
          </Typography>
        </Tooltip>
        <Tooltip title="Re-run all checks">
          <IconButton size="small" onClick={onRefresh} disabled={loading}>
            {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
}

function ReadinessBadge({ label, score }) {
  const theme = useTheme();
  const color = _scoreColor(score, theme);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box sx={{
        width: 10, height: 10, borderRadius: "50%", bgcolor: color,
      }} />
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {label}: <span style={{ color }}>{_scoreLabel(score)}</span>
      </Typography>
    </Box>
  );
}

// ─── Actions panel (Phase A) ────────────────────────────────────────────────

function ActionsPanel({ findings, onFix, onBookSession }) {
  const [filter, setFilter] = useState("all");  // 'all' | 'critical' | 'warning'
  const filtered = (findings || []).filter((f) => {
    if (filter === "all") return true;
    return f.severity === filter;
  });
  const nCritical = (findings || []).filter((f) => f.severity === "critical").length;
  const nWarning = (findings || []).filter((f) => f.severity === "warning").length;

  if (!findings || findings.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 36, color: "success.main", mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No data quality issues detected.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
          Suggested actions
        </Typography>
        <Chip
          label={`Critical ${nCritical}`} size="small"
          variant={filter === "critical" ? "filled" : "outlined"}
          color={filter === "critical" ? "error" : "default"}
          onClick={() => setFilter(filter === "critical" ? "all" : "critical")}
          sx={{ fontSize: 11 }}
        />
        <Chip
          label={`Warning ${nWarning}`} size="small"
          variant={filter === "warning" ? "filled" : "outlined"}
          color={filter === "warning" ? "warning" : "default"}
          onClick={() => setFilter(filter === "warning" ? "all" : "warning")}
          sx={{ fontSize: 11 }}
        />
      </Box>
      <Stack spacing={1}>
        {filtered.map((f, i) => (
          <ActionCard key={`${f.finding_id}-${f.table}-${i}`} finding={f}
                      onFix={onFix} onBookSession={onBookSession} />
        ))}
      </Stack>
    </Box>
  );
}

function ActionCard({ finding, onFix, onBookSession }) {
  const theme = useTheme();
  const isCritical = finding.severity === "critical";
  const sevColor = isCritical ? theme.palette.error.main : theme.palette.warning.main;
  const SevIcon = isCritical ? ErrorOutlineIcon : WarningAmberIcon;
  const canAutoFix = Boolean(finding.fix_recipe_id);
  return (
    <Paper variant="outlined" sx={{
      p: 1.5, borderLeft: `3px solid ${sevColor}`,
    }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <SevIcon sx={{ color: sevColor, fontSize: 18, mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500}>
            {finding.title || finding.finding_id}
          </Typography>
          {finding.detail && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {finding.detail}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap" }}>
            <Chip label={finding.table} size="small" variant="outlined"
                  sx={{ fontSize: 10, height: 20 }} />
            {(finding.columns || []).map((c) => (
              <Chip key={c} label={c} size="small" variant="outlined"
                    sx={{ fontSize: 10, height: 20, fontFamily: "monospace" }} />
            ))}
          </Box>
        </Box>
        <Stack direction="column" spacing={0.5} sx={{ ml: 1, flexShrink: 0 }}>
          {canAutoFix ? (
            <Button size="small" variant="outlined"
              onClick={() => onFix && onFix(finding)}
              sx={{ textTransform: "none", fontSize: 11, py: 0.25, minWidth: 86 }}>
              Fix in chat
            </Button>
          ) : (
            <Button size="small" variant="text" color="inherit" disabled
              sx={{ textTransform: "none", fontSize: 11, py: 0.25, minWidth: 86 }}>
              No auto-fix
            </Button>
          )}
          <Button size="small" variant="text"
            startIcon={<CalendarMonthIcon sx={{ fontSize: 14 }} />}
            onClick={() => onBookSession && onBookSession(finding)}
            sx={{ textTransform: "none", fontSize: 10.5, py: 0.25, minWidth: 86 }}>
            Get help
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}


function DataChat({ onTableCommitted, onClose, targetTable }) {
  const theme = useTheme();
  // Loading data / chatting here are writes — blocked when viewing read-only.
  const { viewAsReadOnly, actingUser } = useImpersonation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState(null);
  const [stagedFile, setStagedFile] = useState(null);
  const [staging, setStaging] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, statusText]);

  const sendMessage = useCallback(async (msg, fileKey) => {
    if (sending) return;
    setSending(true);
    setError(null);
    setStatusText("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);

    try {
      await sendDataChatMessage({ message: msg, file_key: fileKey || undefined }, (event) => {
        if (event.type === "token") {
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === "assistant" && last.streaming) {
              return [...prev.slice(0, -1), { ...last, content: last.content + event.text }];
            }
            return [...prev, { role: "assistant", content: event.text, streaming: true }];
          });
        } else if (event.type === "message_end") {
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            return last?.streaming ? [...prev.slice(0, -1), { role: last.role, content: last.content }] : prev;
          });
          setStatusText("");
        } else if (event.type === "status") {
          setStatusText(event.text);
        } else if (event.type === "done") {
          if (event.committed) {
            setStagedFile(null);
            onTableCommitted(event.committed.table_name);
          }
        } else if (event.type === "error") {
          setError(event.error || "Something went wrong");
        }
      });
    } catch (e) {
      setError(e.message || "Something went wrong");
    } finally {
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        return last?.streaming ? [...prev.slice(0, -1), { role: last.role, content: last.content }] : prev;
      });
      setStatusText("");
      setSending(false);
    }
  }, [sending, onTableCommitted]);

  const handleSend = () => {
    if (viewAsReadOnly) return;
    const msg = input.trim() || (stagedFile ? "Load this file" : "");
    if (!msg) return;
    setInput("");
    sendMessage(msg, stagedFile?.file_key);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const onDrop = useCallback(async (accepted) => {
    if (viewAsReadOnly) return;   // read-only view-as: no uploads
    if (!accepted.length || staging || sending) return;
    const file = accepted[0];
    setStaging(true);
    setError(null);
    try {
      const result = await stageClientCsv(file);
      setStagedFile(result);
      sendMessage(
        targetTable ? `Load this file into the ${targetTable} table` : "Load this file",
        result.file_key
      );
    } catch (e) {
      setError(e.message || "Failed to stage file");
    } finally {
      setStaging(false);
    }
  }, [staging, sending, sendMessage, targetTable, viewAsReadOnly]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
    multiple: false,
    noClick: true,
    noKeyboard: true,
  });

  const isEmpty = messages.length === 0;

  return (
    <Box
      {...getRootProps()}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: isDragActive ? `${theme.palette.primary.main}06` : "background.default",
        border: isDragActive ? `2px dashed ${theme.palette.primary.main}` : "none",
        transition: "all 0.2s",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <input {...getInputProps()} />

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", px: 2.5, py: 1.5, gap: 1, bgcolor: "background.paper", borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          {targetTable ? `Add data to ${targetTable}` : "Load Data"}
        </Typography>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Drag overlay */}
      {isDragActive && (
        <Box sx={{ position: "absolute", inset: 0, zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: `${theme.palette.primary.main}10`, pointerEvents: "none" }}>
          <CloudUploadIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
          <Typography variant="subtitle1" fontWeight={600} color="primary.main">Drop to load</Typography>
        </Box>
      )}

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {isEmpty && !staging && (
          <Box sx={{ textAlign: "center", py: 6, color: "text.disabled" }}>
            <CloudUploadIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2">Drop a CSV file or type a message to get started</Typography>
          </Box>
        )}
        {messages.map((m, i) => <ChatBubble key={i} message={m} />)}
        {statusText && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary", mb: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption">{statusText}</Typography>
          </Box>
        )}
        {staging && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary", mb: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption">Reading file…</Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>{error}</Alert>}
        <div ref={bottomRef} />
      </Box>

      <Divider />

      {/* Staged file chip */}
      {stagedFile && (
        <Box sx={{ px: 2, pt: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            icon={<AttachFileIcon />}
            label={`${stagedFile.filename} (${stagedFile.row_count?.toLocaleString()} rows)`}
            size="small"
            onDelete={() => setStagedFile(null)}
          />
        </Box>
      )}

      {/* Input */}
      {viewAsReadOnly && (
        <Typography variant="caption" sx={{ display: "block", textAlign: "center", color: "text.secondary", pt: 1 }}>
          Read-only — viewing {actingUser?.email || "this user"}. Loading data is disabled.
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, p: 1.5 }}>
        <Tooltip title={viewAsReadOnly ? "Read-only view — uploads disabled" : "Attach CSV"}>
          <span>
            <IconButton size="small" onClick={open} disabled={sending || staging || viewAsReadOnly}>
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder={viewAsReadOnly ? "Read-only view — loading data disabled" : "Type a message or drop a CSV file…"}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending || staging || viewAsReadOnly}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
        />
        <IconButton
          onClick={handleSend}
          disabled={sending || staging || viewAsReadOnly || (!input.trim() && !stagedFile)}
          sx={{ bgcolor: "primary.main", color: "#fff", "&:hover": { bgcolor: "primary.dark" }, "&.Mui-disabled": { bgcolor: "action.disabledBackground" } }}
        >
          {sending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SendIcon fontSize="small" />}
        </IconButton>
      </Box>
    </Box>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Data() {
  const [tables, setTables] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingTables, setLoadingTables] = useState(true);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [loadDialogTarget, setLoadDialogTarget] = useState(null);
  // Phase A: readiness state
  const [tab, setTab] = useState(0);  // 0 = Overview, 1 = Data Admin
  const [readiness, setReadiness] = useState(null);          // { report, from_cache }
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessError, setReadinessError] = useState(null);
  // Phase B: fix drawer state
  const [fixFinding, setFixFinding] = useState(null);  // null = drawer closed
  // Dictionary state (Data Admin tab content)
  const [dictionary, setDictionary] = useState(null);
  const [dictLoading, setDictLoading] = useState(false);
  const [dictError, setDictError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoadingTables(true);
    try {
      const [tablesResult, historyResult] = await Promise.allSettled([
        listClientTables(),
        listDataHistory(),
      ]);
      if (tablesResult.status === "fulfilled") {
        setTables(tablesResult.value.tables || []);
      }
      if (historyResult.status === "fulfilled") {
        setHistory(historyResult.value.history || []);
      }
    } finally {
      setLoadingTables(false);
    }
  }, []);

  const fetchReadiness = useCallback(async ({ refresh = false } = {}) => {
    setReadinessLoading(true);
    setReadinessError(null);
    try {
      const data = await getDataReadiness({ refresh });
      setReadiness(data);
    } catch (e) {
      setReadinessError(e?.response?.data?.error || e.message || "Could not fetch readiness");
    } finally {
      setReadinessLoading(false);
    }
  }, []);

  const fetchDictionary = useCallback(async () => {
    setDictLoading(true);
    setDictError(null);
    try {
      const data = await getDataDictionary();
      setDictionary(data);
    } catch (e) {
      setDictError(e?.response?.data?.error || e.message || "Could not fetch dictionary");
    } finally {
      setDictLoading(false);
    }
  }, []);

  // Inline description edit. Optimistic — patches local state immediately,
  // then calls the backend. On error, reverts and re-throws so the row's
  // editor can show the message and keep the draft.
  const handleSaveDescription = useCallback(async ({ table, column, description }) => {
    let prev = null;
    setDictionary((d) => {
      if (!d) return d;
      const tables = (d.tables || []).map((t) => {
        if ((t.name || "").toLowerCase() !== (table || "").toLowerCase()) return t;
        if (column == null) {
          prev = { kind: "table", value: t.description };
          return { ...t, description };
        }
        return {
          ...t,
          columns: (t.columns || []).map((c) => {
            if ((c.name || "").toLowerCase() !== (column || "").toLowerCase()) return c;
            prev = { kind: "column", value: c.description };
            return { ...c, description };
          }),
        };
      });
      return { ...d, tables };
    });
    try {
      await patchDataDictionary({ table, column, description });
    } catch (err) {
      // Revert on error
      setDictionary((d) => {
        if (!d || !prev) return d;
        const tables = (d.tables || []).map((t) => {
          if ((t.name || "").toLowerCase() !== (table || "").toLowerCase()) return t;
          if (prev.kind === "table") return { ...t, description: prev.value };
          return {
            ...t,
            columns: (t.columns || []).map((c) =>
              (c.name || "").toLowerCase() === (column || "").toLowerCase()
                ? { ...c, description: prev.value } : c
            ),
          };
        });
        return { ...d, tables };
      });
      throw err;
    }
  }, []);

  // Batch-accept many drafts for one table with a SINGLE backend write (one GCS
  // mutation), avoiding the per-object 429 that N sequential PATCHes hit.
  const handleSaveDescriptionsBatch = useCallback(async ({ table, edits }) => {
    if (!edits || !edits.length) return;
    const byCol = new Map(
      edits.filter((e) => e.column != null)
           .map((e) => [(e.column || "").toLowerCase(), e.description])
    );
    const tableEdit = edits.find((e) => e.column == null);
    let snapshot = null;
    setDictionary((d) => {
      if (!d) return d;
      snapshot = d;  // pre-update tree, for revert on failure
      const tables = (d.tables || []).map((t) => {
        if ((t.name || "").toLowerCase() !== (table || "").toLowerCase()) return t;
        const nt = { ...t };
        if (tableEdit) nt.description = tableEdit.description;
        nt.columns = (t.columns || []).map((c) => {
          const nd = byCol.get((c.name || "").toLowerCase());
          return nd != null ? { ...c, description: nd } : c;
        });
        return nt;
      });
      return { ...d, tables };
    });
    try {
      await patchDataDictionaryBatch({ table, edits });
    } catch (err) {
      if (snapshot) setDictionary(snapshot);  // revert optimistic update
      throw err;
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    // Wait for tables to load before measuring readiness (no point otherwise).
    if (!loadingTables && tables.length > 0) {
      fetchReadiness();
      fetchDictionary();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTables, tables.length]);

  // Phase B: open the fix drawer with the chosen finding.
  const handleFix = useCallback((finding) => {
    if (!finding?.fix_recipe_id) return;
    setFixFinding(finding);
  }, []);

  // When a fix applies successfully, re-fetch readiness so the action
  // disappears from the panel.
  const handleFixApplied = useCallback(() => {
    fetchReadiness({ refresh: true });
  }, [fetchReadiness]);

  const handleBookSession = useCallback((finding) => {
    // bookingUrlFor picks the right event type per finding and adds
    // ?a1=<finding_id>&a2=<table> for Calendly's question pre-fill.
    window.open(bookingUrlFor(finding), "_blank", "noopener");
  }, []);

  const handleTableCommitted = useCallback(() => {
    setLoadDialogOpen(false);
    setLoadDialogTarget(null);
    fetchData();
  }, [fetchData]);

  const historyByTable = {};
  for (const entry of history) {
    if (!historyByTable[entry.table_name]) historyByTable[entry.table_name] = [];
    historyByTable[entry.table_name].push(entry);
  }

  return (
    <Box sx={{ maxWidth: 960, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>Data</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setLoadDialogOpen(true)}
          sx={{ textTransform: "none", borderRadius: 1.5, boxShadow: "none" }}
        >
          New Data
        </Button>
      </Box>

      {/* Tabs — Dictionary (business view) + Quality (admin view) */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, minHeight: 36 }}>
        <Tab label="Dictionary" sx={{ textTransform: "none", minHeight: 36, py: 0.5 }} />
        <Tab label="Quality" sx={{ textTransform: "none", minHeight: 36, py: 0.5 }} />
      </Tabs>

      {/* === Dictionary Tab === */}
      {tab === 0 && (
        <>
          {/* Loading skeleton */}
          {loadingTables && (
            <Stack spacing={1.5}>
              <Skeleton variant="rounded" height={72} />
              <Skeleton variant="rounded" height={72} />
            </Stack>
          )}

          {/* Empty state */}
          {!loadingTables && tables.length === 0 && (
            <Box sx={{ textAlign: "center", py: 12, color: "text.disabled" }}>
              <StorageIcon sx={{ fontSize: 56, mb: 2 }} />
              <Typography variant="h6" fontWeight={600} color="text.secondary">No data loaded yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                Load a CSV file to get started
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setLoadDialogOpen(true)}
                sx={{ textTransform: "none", borderRadius: 1.5, boxShadow: "none" }}
              >
                Add your first dataset
              </Button>
            </Box>
          )}

          {/* Populated state */}
          {!loadingTables && tables.length > 0 && (
            <DictionarySection
              dictionary={dictionary}
              loading={dictLoading}
              error={dictError}
              readiness={readiness}
              onJumpToQuality={() => setTab(1)}
              onSaveDescription={handleSaveDescription}
              onSaveDescriptionsBatch={handleSaveDescriptionsBatch}
            />
          )}
        </>
      )}

      {/* === Quality Tab === */}
      {tab === 1 && (
        <QualityTab
          history={history}
          readiness={readiness}
          readinessLoading={readinessLoading}
          readinessError={readinessError}
          onRefreshReadiness={() => fetchReadiness({ refresh: true })}
          onFix={handleFix}
          onBookSession={handleBookSession}
        />
      )}

      {/* Fix drawer (Phase B) */}
      <FixDrawer
        open={Boolean(fixFinding)}
        finding={fixFinding}
        onClose={() => setFixFinding(null)}
        onApplied={handleFixApplied}
      />

      {/* Load data dialog */}
      <Dialog
        open={loadDialogOpen}
        onClose={() => { setLoadDialogOpen(false); setLoadDialogTarget(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { height: 560, display: "flex", flexDirection: "column", overflow: "hidden" } }}
      >
        <DataChat
          onTableCommitted={handleTableCommitted}
          onClose={() => { setLoadDialogOpen(false); setLoadDialogTarget(null); }}
          targetTable={loadDialogTarget}
        />
      </Dialog>
    </Box>
  );
}
